/**
 * Phase 2A: Album Art Service for Songs Layer
 * Fetches album artwork from multiple sources for enhanced Songs metadata
 */

export interface AlbumArtResult {
  url: string;
  size: string;
  source: 'itunes' | 'lastfm' | 'spotify';
  quality: 'low' | 'medium' | 'high';
}

export interface SongSearchData {
  songName: string;
  artistName: string;
  albumName?: string;
}

class AlbumArtService {
  private cache = new Map<string, AlbumArtResult>();

  /**
   * Main method to fetch album art with fallback sources
   */
  async fetchAlbumArt(
    songData: SongSearchData
  ): Promise<AlbumArtResult | null> {
    const cacheKey =
      `${songData.songName}-${songData.artistName}`.toLowerCase();

    // Check cache first
    if (this.cache.has(cacheKey)) {
      console.log('[ALBUM ART] Using cached result for:', songData.songName);
      return this.cache.get(cacheKey)!;
    }

    console.log('[ALBUM ART] Fetching album art for:', songData);

    try {
      // Try iTunes API first (no API key required, good coverage)
      const iTunesResult = await this.fetchFromItunes(songData);
      if (iTunesResult) {
        this.cache.set(cacheKey, iTunesResult);
        return iTunesResult;
      }

      // Try alternative search patterns if the main search failed
      if (songData.artistName && songData.songName) {
        console.log('[ALBUM ART] Trying alternative search patterns...');

        // ENHANCED: Try album + artist search first (most likely to get correct album art)
        if (songData.albumName) {
          const albumArtistResult = await this.fetchFromItunes({
            songName: '',
            artistName: songData.artistName,
            albumName: songData.albumName,
          });
          if (albumArtistResult) {
            console.log('[ALBUM ART] Found result using album + artist search');
            this.cache.set(cacheKey, albumArtistResult);
            return albumArtistResult;
          }
        }

        // Try just artist name
        const artistOnlyResult = await this.fetchFromItunes({
          songName: '',
          artistName: songData.artistName,
          albumName: songData.albumName,
        });
        if (artistOnlyResult) {
          console.log('[ALBUM ART] Found result using artist-only search');
          this.cache.set(cacheKey, artistOnlyResult);
          return artistOnlyResult;
        }

        // ENHANCED: Try collaboration patterns (for "Artist & Artist" etc.)
        if (
          songData.artistName.includes('&') ||
          songData.artistName.includes('feat') ||
          songData.artistName.includes('ft.')
        ) {
          console.log(
            '[ALBUM ART] Detected collaboration, trying primary artist searches...'
          );

          // Extract primary artist (first artist before &, feat, ft.)
          const primaryArtist = this.extractPrimaryArtist(songData.artistName);
          if (primaryArtist !== songData.artistName) {
            const primaryResult = await this.fetchFromItunes({
              songName: songData.songName,
              artistName: primaryArtist,
              albumName: songData.albumName,
            });
            if (primaryResult) {
              console.log(
                '[ALBUM ART] Found result using primary artist:',
                primaryArtist
              );
              this.cache.set(cacheKey, primaryResult);
              return primaryResult;
            }
          }
        }

        // ENHANCED CLEANING: Better handling of extracted song data
        const cleanedSongData = {
          songName: this.cleanSongName(songData.songName),
          artistName: this.cleanArtistName(songData.artistName),
          albumName: songData.albumName,
        };

        if (
          cleanedSongData.songName !== songData.songName ||
          cleanedSongData.artistName !== songData.artistName
        ) {
          console.log(
            '[ALBUM ART] Trying cleaned search terms:',
            cleanedSongData
          );
          const cleanedResult = await this.fetchFromItunes(cleanedSongData);
          if (cleanedResult) {
            console.log('[ALBUM ART] Found result using cleaned search terms');
            this.cache.set(cacheKey, cleanedResult);
            return cleanedResult;
          }
        }
      }

      // Could add additional sources here (Last.fm, Spotify, etc.)
      console.log(
        '[ALBUM ART] No album art found for:',
        songData.songName,
        'by',
        songData.artistName
      );
      return null;
    } catch (error) {
      console.warn('[ALBUM ART] Error fetching album art:', error);
      return null;
    }
  }

  /**
   * Fetch from iTunes API
   */
  private async fetchFromItunes(
    songData: SongSearchData
  ): Promise<AlbumArtResult | null> {
    try {
      // Build search query with enhanced logic
      let searchTerm = '';
      let entity = 'song';

      if (songData.songName && songData.artistName) {
        // Standard song + artist search
        searchTerm = `${songData.songName} ${songData.artistName}`;
      } else if (songData.albumName && songData.artistName) {
        // Album + artist search (useful when no song name)
        searchTerm = `${songData.albumName} ${songData.artistName}`;
        entity = 'album'; // Search for albums instead of songs
      } else if (songData.artistName) {
        // Artist-only search
        searchTerm = songData.artistName;
        entity = 'album'; // Search for albums by artist
      } else {
        // Fallback to song name only
        searchTerm = songData.songName;
      }

      const url = `https://itunes.apple.com/search?term=${encodeURIComponent(
        searchTerm
      )}&media=music&limit=10&entity=${entity}`;

      console.log('[ALBUM ART] iTunes API request:', url);

      const response = await fetch(url);
      if (!response.ok) {
        console.warn('[ALBUM ART] iTunes API error:', response.status);
        return null;
      }

      const data = await response.json();

      if (!data.results || data.results.length === 0) {
        console.log('[ALBUM ART] No iTunes results found');
        return null;
      }

      // Find best match
      const bestMatch = this.findBestMatch(data.results, songData);
      if (!bestMatch || (!bestMatch.artworkUrl100 && !bestMatch.artworkUrl60)) {
        console.log('[ALBUM ART] No suitable iTunes match found');
        return null;
      }

      // Get high-resolution artwork URL (handle both song and album results)
      const baseArtworkUrl =
        bestMatch.artworkUrl100 ||
        bestMatch.artworkUrl60 ||
        bestMatch.artworkUrl512;
      const artworkUrl = this.getHighResolutionUrl(baseArtworkUrl);

      // CRITICAL FIX: Validate URL before returning
      if (!artworkUrl || !this.isValidImageUrl(artworkUrl)) {
        console.warn(
          '[ALBUM ART] Generated artwork URL is invalid:',
          artworkUrl
        );
        return null;
      }

      console.log('[ALBUM ART] iTunes match found:', {
        track: bestMatch.trackName || bestMatch.collectionName,
        artist: bestMatch.artistName,
        album: bestMatch.collectionName || bestMatch.albumName,
        artwork: artworkUrl,
      });

      return {
        url: artworkUrl,
        size: '600x600',
        source: 'itunes',
        quality: 'high',
      };
    } catch (error) {
      console.warn('[ALBUM ART] iTunes fetch error:', error);
      return null;
    }
  }

  /**
   * Find the best matching result from iTunes API with enhanced scoring
   */
  private findBestMatch(results: any[], songData: SongSearchData): any | null {
    // Enhanced scoring algorithm to find best match
    let bestMatch = null;
    let bestScore = 0;

    console.log(
      '[ALBUM ART] Evaluating',
      results.length,
      'iTunes results for best match'
    );

    for (const result of results) {
      let score = 0;

      // Check track name match
      if (result.trackName) {
        const trackSimilarity = this.calculateSimilarity(
          songData.songName.toLowerCase(),
          result.trackName.toLowerCase()
        );
        score += trackSimilarity * 3; // Weight track name heavily

        // BONUS: Exact match gets extra points
        if (
          result.trackName.toLowerCase() === songData.songName.toLowerCase()
        ) {
          score += 1;
        }
      }

      // Check artist name match
      if (result.artistName && songData.artistName) {
        const artistSimilarity = this.calculateSimilarity(
          songData.artistName.toLowerCase(),
          result.artistName.toLowerCase()
        );
        score += artistSimilarity * 2; // Weight artist name moderately

        // BONUS: Exact match gets extra points
        if (
          result.artistName.toLowerCase() === songData.artistName.toLowerCase()
        ) {
          score += 0.5;
        }
      }

      // Check album name match if available - ENHANCED WEIGHTING
      const albumField = result.collectionName || result.albumName;
      if (albumField && songData.albumName) {
        const albumSimilarity = this.calculateSimilarity(
          songData.albumName.toLowerCase(),
          albumField.toLowerCase()
        );
        score += albumSimilarity * 2; // Increased weight for album name match

        // BONUS: Exact album match gets significant bonus
        if (albumField.toLowerCase() === songData.albumName.toLowerCase()) {
          score += 1;
        }
      }

      // PENALTY: Reduce score for "Live" versions unless specifically requested
      if (
        result.trackName &&
        result.trackName.toLowerCase().includes('live') &&
        !songData.songName.toLowerCase().includes('live')
      ) {
        score -= 0.5;
        console.log(
          '[ALBUM ART] Applied penalty for Live version:',
          result.trackName
        );
      }

      // PENALTY: Reduce score for "Remix" versions unless specifically requested
      if (
        result.trackName &&
        result.trackName.toLowerCase().includes('remix') &&
        !songData.songName.toLowerCase().includes('remix')
      ) {
        score -= 0.3;
        console.log(
          '[ALBUM ART] Applied penalty for Remix version:',
          result.trackName
        );
      }

      // PENALTY: Heavily reduce score for DJ mixes and compilations
      if (
        albumField.toLowerCase().includes('dj mix') ||
        albumField.toLowerCase().includes('mixed') ||
        albumField.toLowerCase().includes('compilation') ||
        albumField.toLowerCase().includes("today's hits") ||
        albumField.toLowerCase().includes('pride') ||
        albumField.toLowerCase().includes('pov:') ||
        result.trackName?.toLowerCase().includes('mixed')
      ) {
        score -= 2.5; // Very heavy penalty for DJ mixes/compilations
        console.log(
          '[ALBUM ART] Applied very heavy penalty for DJ Mix/Compilation:',
          albumField
        );
      }

      // BONUS: Prefer proper albums and singles over compilations
      if (
        albumField.toLowerCase().includes('single') ||
        albumField.toLowerCase().includes('album') ||
        albumField.toLowerCase().includes('ep')
      ) {
        score += 0.5;
        console.log(
          '[ALBUM ART] Applied bonus for proper release format:',
          albumField
        );
      }

      // BONUS: Prefer albums that match or are close to the artist's name
      if (albumField && songData.artistName) {
        if (
          albumField
            .toLowerCase()
            .includes(songData.artistName.toLowerCase()) ||
          songData.artistName.toLowerCase().includes(albumField.toLowerCase())
        ) {
          score += 0.3;
          console.log(
            '[ALBUM ART] Applied bonus for artist-album name correlation:',
            albumField
          );
        }
      }

      // MAJOR BONUS: Prioritize official Apple Music releases over fan compilations
      if (result.artistName && songData.artistName) {
        const resultArtist = result.artistName.toLowerCase();
        const searchArtist = songData.artistName.toLowerCase();

        // Check if this is the primary artist (not a cover/tribute artist)
        if (
          resultArtist === searchArtist ||
          resultArtist.includes(searchArtist) ||
          searchArtist.includes(resultArtist)
        ) {
          // Additional bonus for exact artist match
          if (resultArtist === searchArtist) {
            score += 1.0;
            console.log(
              '[ALBUM ART] Applied major bonus for exact primary artist match:',
              result.artistName
            );
          } else {
            score += 0.7;
            console.log(
              '[ALBUM ART] Applied bonus for primary artist match:',
              result.artistName
            );
          }

          // SUPER BONUS: Official album from the primary artist
          if (albumField && !this.isCompilationAlbum(albumField)) {
            score += 1.5;
            console.log(
              '[ALBUM ART] Applied super bonus for official primary artist album:',
              albumField
            );
          }
        }
      }

      // PENALTY: Reduce score for obvious cover/tribute artists
      if (result.artistName && songData.artistName) {
        // Detect cover/tribute artists by common patterns
        if (this.isCoverArtist(result.artistName, songData.artistName)) {
          score -= 1.5;
          console.log(
            '[ALBUM ART] Applied penalty for cover/tribute artist:',
            result.artistName
          );
        }
      }

      console.log('[ALBUM ART] Match candidate:', {
        track: result.trackName || result.collectionName,
        artist: result.artistName,
        album: result.collectionName || result.albumName,
        score: score.toFixed(2),
      });

      if (score > bestScore) {
        bestScore = score;
        bestMatch = result;
      }
    }

    console.log(
      '[ALBUM ART] Best match selected:',
      bestMatch
        ? {
            track: bestMatch.trackName || bestMatch.collectionName,
            artist: bestMatch.artistName,
            album: bestMatch.collectionName || bestMatch.albumName,
            score: bestScore.toFixed(2),
          }
        : 'No suitable match found'
    );

    // Only return match if score is reasonable
    return bestScore > 0.4 ? bestMatch : null; // Slightly higher threshold
  }

  /**
   * Calculate string similarity (simple implementation)
   */
  private calculateSimilarity(str1: string, str2: string): number {
    // Simple substring matching for now
    if (str1 === str2) return 1.0;
    if (str1.includes(str2) || str2.includes(str1)) return 0.8;

    // Check for partial word matches
    const words1 = str1.split(/\s+/);
    const words2 = str2.split(/\s+/);
    let matchingWords = 0;

    for (const word1 of words1) {
      for (const word2 of words2) {
        if (
          word1.length > 2 &&
          word2.length > 2 &&
          (word1.includes(word2) || word2.includes(word1))
        ) {
          matchingWords++;
          break;
        }
      }
    }

    return matchingWords / Math.max(words1.length, words2.length);
  }

  /**
   * Convert iTunes artwork URL to higher resolution with Apple CDN support
   */
  private getHighResolutionUrl(artworkUrl: string): string {
    // Handle Apple CDN URLs with multiple format support
    if (artworkUrl.includes('mzstatic.com')) {
      // Apple CDN URLs can end with /SIZExSIZEbb.FORMAT
      // Replace .webp with .jpg for better compatibility, keep .png as-is
      let processedUrl = artworkUrl;

      // Convert WebP to JPG for better browser compatibility
      if (processedUrl.includes('.webp')) {
        processedUrl = processedUrl.replace(/\.webp$/, '.jpg');
        console.log(
          '[ALBUM ART] Apple CDN: converted .webp to .jpg for compatibility'
        );
      }

      // Handle different size patterns in Apple CDN URLs
      processedUrl = processedUrl
        .replace(/\/100x100bb\.(jpg|png)$/, '/600x600bb.$1')
        .replace(/\/60x60bb\.(jpg|png)$/, '/600x600bb.$1')
        .replace(/\/30x30bb\.(jpg|png)$/, '/600x600bb.$1')
        .replace(/\/632x632bb\.(jpg|png)$/, '/600x600bb.$1'); // Slightly smaller for better loading

      console.log('[ALBUM ART] Apple CDN URL processed:', {
        original: artworkUrl,
        processed: processedUrl,
      });
      return processedUrl;
    }

    // Legacy iTunes URLs (fallback for older format)
    return artworkUrl
      .replace(/100x100/, '600x600')
      .replace(/60x60/, '600x600')
      .replace(/30x30/, '600x600');
  }

  /**
   * Clean song name for better iTunes API search results
   * Removes common patterns that interfere with search
   */
  private cleanSongName(songName: string): string {
    return songName
      .replace(/^["'`]|["'`]$/g, '') // Remove leading/trailing quotes
      .replace(/\s*song\s*$/i, '') // Remove " song" suffix
      .replace(/\s*track\s*$/i, '') // Remove " track" suffix
      .replace(/\s*-\s*official\s*/i, '') // Remove "- official"
      .replace(/\s*\(official[^)]*\)/i, '') // Remove "(official...)"
      .replace(/\s*\[official[^\]]*\]/i, '') // Remove "[official...]"
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  }

  /**
   * Clean artist name for better iTunes API search results
   * Removes common patterns that interfere with search
   */
  private cleanArtistName(artistName: string): string {
    return artistName
      .replace(/^["'`]|["'`]$/g, '') // Remove leading/trailing quotes
      .replace(/\s*-\s*topic$/i, '') // Remove "- Topic" suffix
      .replace(/\s*official$/i, '') // Remove "Official" suffix
      .replace(/\s*\(official[^)]*\)/i, '') // Remove "(official...)"
      .replace(/\s*\[official[^\]]*\]/i, '') // Remove "[official...]"
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  }

  /**
   * Extract primary artist from collaboration strings
   * Handles patterns like "Artist1 & Artist2", "Artist1 feat. Artist2", etc.
   */
  private extractPrimaryArtist(artistName: string): string {
    // Split on common collaboration patterns
    const patterns = [
      /\s+&\s+/i, // " & "
      /\s+feat\.?\s+/i, // " feat " or " feat. "
      /\s+ft\.?\s+/i, // " ft " or " ft. "
      /\s+featuring\s+/i, // " featuring "
      /\s+with\s+/i, // " with "
      /\s+x\s+/i, // " x " (collaboration marker)
      /\s*,\s*/, // ", " (comma separation)
    ];

    for (const pattern of patterns) {
      if (pattern.test(artistName)) {
        const primaryArtist = artistName.split(pattern)[0].trim();
        if (primaryArtist.length > 0 && primaryArtist !== artistName) {
          console.log(
            '[ALBUM ART] Extracted primary artist:',
            primaryArtist,
            'from:',
            artistName
          );
          return primaryArtist;
        }
      }
    }

    return artistName; // Return original if no patterns found
  }

  /**
   * Validate that a URL is a proper image URL
   */
  private isValidImageUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);

      // Check if it's HTTPS
      if (urlObj.protocol !== 'https:') {
        return false;
      }

      // Check if it's from a trusted domain
      const trustedDomains = [
        'is1-ssl.mzstatic.com',
        'is2-ssl.mzstatic.com',
        'is3-ssl.mzstatic.com',
        'is4-ssl.mzstatic.com',
        'is5-ssl.mzstatic.com',
      ];
      if (!trustedDomains.some(domain => urlObj.hostname.includes(domain))) {
        console.warn('[ALBUM ART] URL from untrusted domain:', urlObj.hostname);
        return false;
      }

      // Check if it looks like an image URL
      const pathname = urlObj.pathname.toLowerCase();
      if (
        !pathname.includes('.jpg') &&
        !pathname.includes('.jpeg') &&
        !pathname.includes('.png')
      ) {
        console.warn(
          '[ALBUM ART] URL does not appear to be an image:',
          pathname
        );
        return false;
      }

      return true;
    } catch (error) {
      console.warn('[ALBUM ART] Invalid URL format:', url, error);
      return false;
    }
  }

  /**
   * Clear the cache (useful for testing)
   */
  clearCache(): void {
    this.cache.clear();
    console.log('[ALBUM ART] Cache cleared');
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  /**
   * Check if an album is likely a compilation/tribute album
   */
  private isCompilationAlbum(albumName: string): boolean {
    const compilationPatterns = [
      'compilation',
      'various artists',
      'tribute',
      'cover',
      'hits collection',
      'best of',
      'greatest hits',
      'karaoke',
      'instrumental',
      'piano version',
      'acoustic version',
      'originally performed by',
      'in the style of',
      'dj mix',
      'mixed',
      "today's hits",
      'pride',
      'pov:',
      'workout',
      'fitness',
    ];

    const lowerAlbum = albumName.toLowerCase();
    return compilationPatterns.some(pattern => lowerAlbum.includes(pattern));
  }

  /**
   * Check if an artist is likely a cover/tribute artist
   */
  private isCoverArtist(resultArtist: string, searchArtist: string): boolean {
    const lowerResult = resultArtist.toLowerCase();
    const lowerSearch = searchArtist.toLowerCase();

    // If artists are similar, not a cover artist
    if (
      lowerResult === lowerSearch ||
      lowerResult.includes(lowerSearch) ||
      lowerSearch.includes(lowerResult)
    ) {
      return false;
    }

    // Check for cover artist patterns
    const coverPatterns = [
      'tribute',
      'cover',
      'karaoke',
      'instrumental',
      'piano',
      'acoustic',
      'originally performed',
      'in the style',
      'sound alike',
      'backing track',
      'sing karaoke',
      'gentle groove',
      'strongman',
      'isimgkeys',
      'edkara',
      '3 dope brothas',
      'songs of deliverance',
    ];

    return coverPatterns.some(pattern => lowerResult.includes(pattern));
  }
}

// Export singleton instance
export const albumArtService = new AlbumArtService();
export default albumArtService;
